import {Link} from "react-router";
import {twJoin} from "tailwind-merge";
import {CtaButtonFilled} from "~/components/ctaButtonFilled";
import {useFrontendProxyUrl} from "~/root/rootLoaderData";

/* ============================================================================
 * Hero, ClickUp-style layout, GJ design tokens + buttons, VOS-Intellsys content.
 * ============================================================================ */

const bullets = [
    {lead: "Unify your data.", body: "Built-in connectors for Meta, Google, Amazon, Flipkart + more."},
    {lead: "Run your operations.", body: "CRM, sales, inventory, helpdesk, every function in one OS."},
    {lead: "Activate AI.", body: "AI Agent reads your data and takes action across every workflow."},
];

/* Hero chip strip, most-valued apps, one per category mostly, with a single
 * highlighted clickable "Show more" link to the full /apps page at the end.
 * App chips are non-interactive labels; only "Show more" is a real Link. */
type ChipData = {label: string; href?: string};
const featureChips: Array<ChipData> = [
    {label: "AI Agent"},
    {label: "CRM"},
    {label: "Sales"},
    {label: "Online Store"},
    {label: "Website"},
    {label: "Campaigns"},
    {label: "Content"},
    {label: "Chat"},
    {label: "Analytics"},
    {label: "Connector"},
    {label: "Inventory"},
    {label: "Helpdesk"},
    {label: "Attendance"},
    {label: "Show more", href: "/apps"},
];

function Checkmark() {
    return (
        <svg
            viewBox="0 0 24 24"
            fill="none"
            className="w-4 h-4 shrink-0 mt-[3px] text-primary-500"
            stroke="currentColor"
            strokeWidth="3"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M5 12l5 5L20 7"
            />
        </svg>
    );
}

function FeatureChip({label, href}: ChipData) {
    const baseClasses = "text-nav-text-b px-2 py-1 lg:px-3 lg:py-2 rounded-sm lg:rounded-lg border transition-colors duration-200 whitespace-nowrap inline-flex items-center gap-1.5";

    if (href != null) {
        return (
            <Link
                to={href}
                className={twJoin(baseClasses, "border-primary-500 text-primary-500 bg-primary-200/40 hover:bg-primary-200/60 no-underline")}
            >
                {label}
                <svg
                    viewBox="0 0 24 24"
                    className="w-3.5 h-3.5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.5"
                    aria-hidden="true"
                >
                    <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        d="M13 7l5 5m0 0l-5 5m5-5H6"
                    />
                </svg>
            </Link>
        );
    }

    return <span className={twJoin(baseClasses, "border-surface-lines-and-borders text-text-body bg-transparent")}>{label}</span>;
}

export function Hero() {
    const continueToIntellsysUrl = useFrontendProxyUrl("/u");

    return (
        <section
            aria-labelledby="hero-title"
            className="relative overflow-hidden pt-24 pb-9 lg:py-0 lg:min-h-screen lg:flex lg:items-center"
        >
            <div className="w-full grid grid-cols-1 lg:grid-cols-[5fr_6fr] gap-y-10">
                {/* LEFT COLUMN, left edge aligns with the GJ rail (--gj-px-screen-edge-constrained),
                 * same as Footer, LeadGenBlock, BuiltWithFounders, and the /apps page. */}
                <div className="px-5 lg:pl-[var(--gj-px-screen-edge-constrained)] lg:pr-12">
                    <div className="grid gap-4 max-w-[540px]">
                        <h1
                            id="hero-title"
                            className="text-heading2-25 text-text-primary tracking-tight"
                        >
                            <span className="text-gradient-blue">Intellsys OS</span> to run
                            <br className="hidden lg:block" /> your entire venture.
                        </h1>

                        <ul className="grid gap-2">
                            {bullets.map((b) => (
                                <li
                                    key={b.lead}
                                    className="grid grid-cols-[auto_1fr] gap-2 items-start"
                                >
                                    <Checkmark />
                                    <p className="text-medium text-text-body">
                                        <strong className="text-medium-b text-text-primary font-semibold">{b.lead}</strong> {b.body}
                                    </p>
                                </li>
                            ))}
                        </ul>

                        <div className="flex flex-wrap items-center gap-4 mt-2">
                            <CtaButtonFilled
                                href={continueToIntellsysUrl}
                                text="Continue to Intellsys"
                                arrow={true}
                                className="shadow-[0_4px_12px_rgba(31,64,203,0.18)]"
                            />
                        </div>

                        <div className="grid gap-2 pt-2">
                            <p className="text-badge-text text-text-tertiary uppercase">24 APPS · 5 CATEGORIES · BUILT FOR INDIA</p>
                            <div className="flex flex-wrap gap-1.5">
                                {featureChips.map((c) => (
                                    <FeatureChip
                                        key={c.label}
                                        label={c.label}
                                        href={c.href}
                                    />
                                ))}
                            </div>
                        </div>
                    </div>
                </div>

                {/* RIGHT COLUMN, on mobile uses image's natural 4:3 aspect; on desktop stretches to match text-column height (grid items-stretch default) */}
                <div className="relative overflow-hidden aspect-[4/3] lg:aspect-auto">
                    <img
                        src="https://kanyini-cdn.b-cdn.net/images/Intellsys/Gemini_Generated_Image_tonrg8tonrg8tonr.png"
                        alt="Intellsys OS, venture operating system"
                        className="absolute inset-0 w-full h-full object-cover object-left-top"
                        loading="eager"
                        fetchPriority="high"
                    />
                    <div className="absolute inset-x-0 bottom-0 h-1/4 bg-gradient-to-t from-surface-primary-background to-transparent pointer-events-none" />
                </div>
            </div>
        </section>
    );
}
